import { useCallback, useEffect, useMemo, useState } from 'react';
import { HistoricItem, HistoricOrder, Item, Modifier } from 'olo-service';
import { createHookContext } from '@clover-util/react-context';

import { useCustomerOrders } from 'hooks/useApi/useCustomerOrdersResource';
import { useCustomer } from './customer/customer';
import { useMerchant } from './merchant';
import { NewCartItem, useCart } from './cart';
import { getConciseLineItems, getModifierPrice } from './OrderHistory.util';
import { useGiftCardCart } from './giftCardCart';
import { heapTrack } from 'util/Analytics';
import { useMenu } from 'components/MenuContext/MenuContext';

export enum AddOrderFailedReason {
  ITEM = 'unavailable-items',
  MODIFIER = 'unavailable-modifiers'
}

export type ConciseLineItem = HistoricItem & {
  quantity: number;
}

export const { useOrderHistory, OrderHistoryProvider } = createHookContext('OrderHistory', () => {
  const { merchant, isCustomerAuthEnabled } = useMerchant();
  const { sessionToken } = useCustomer();
  const { add, itemCount: cartItemCount } = useCart();
  const { itemCount: giftCardItemCount } = useGiftCardCart();
  const { menu } = useMenu();
  const { orders, loading, error } = useCustomerOrders(merchant?.merchantUuid, sessionToken, isCustomerAuthEnabled);
  const [unavailableItems, setUnavailableItems] = useState([] as HistoricItem[]);
  const [itemsWithUnavailableModifiers, setItemsWithUnavailableModifiers] = useState([] as HistoricItem[]);
  const [reorderFailureCode, setReorderfailureCode] = useState<string>();
  const totalItemCount = cartItemCount + giftCardItemCount;

  const isLoading = useMemo(() => (loading || (!orders && !!sessionToken)) && !error, [loading, orders, sessionToken, error]);

  const addUnavailableItem = useCallback((value: HistoricItem) => { setUnavailableItems(unavailableItems => [...unavailableItems, value]); }, []);
  const addItemWithUnavailableModifiers = useCallback((value: HistoricItem) => { setItemsWithUnavailableModifiers(itemsWithUnavailableModifiers => [...itemsWithUnavailableModifiers, value]); }, []);
  const removeItemFromUnavailableModifiers = useCallback((value: HistoricItem, list: HistoricItem[]) => {
    setItemsWithUnavailableModifiers( list.filter((item) => { return item.id !== value.id; }) );
  }, []);

  function checkItemsWithUnavailableModifiers(cartItem: NewCartItem) {
    itemsWithUnavailableModifiers.forEach(unavailableItem => {
      if (cartItem.id === unavailableItem.id) removeItemFromUnavailableModifiers(unavailableItem, itemsWithUnavailableModifiers);
    });
  }

  const addHistoricItemsToCart = useCallback((order: HistoricOrder) => {
    const availableItems = Object.entries(getConciseLineItems(order)).filter(([key, item]) => {
      if (!item.availableForOrdering) {
        addUnavailableItem(item);
        if (reorderFailureCode !== AddOrderFailedReason.ITEM) {
          setReorderfailureCode(AddOrderFailedReason.ITEM);
        }
      }

      return item.availableForOrdering && item.modifiers.every(modifier => {
        if (!modifier.availableForOrdering) {
          addItemWithUnavailableModifiers(item);
          if (reorderFailureCode !== AddOrderFailedReason.MODIFIER) {
            setReorderfailureCode(AddOrderFailedReason.MODIFIER);
          }
        }

        return modifier.availableForOrdering;
      });
    });

    availableItems.forEach(([key, item]) => {
      //find product item based on menu to reflect new price in the cart
      const menuItem = menu?.items[item.id] as Item;

      const modifiers = item.modifiers.map(modifier => {
        //find modifier based on menu to reflect new price in the cart
        const modifierPrice = getModifierPrice(menuItem, modifier);

        return {
          id: modifier.id,
          groupId: modifier.modifierGroupId,
          name: modifier.name,
          price: modifierPrice
        };
      });

      menuItem && add({
        id: item.id,
        name: item.name,
        price: menuItem.price,
        quantity: item.quantity,
        modifiers: modifiers as Modifier[],
        note: item.note,
      } as NewCartItem);
    });
  }, [menu, add, addItemWithUnavailableModifiers, addUnavailableItem, reorderFailureCode]);

  const clearUnavailableItems = useCallback(() => {
    setUnavailableItems([]);
    setItemsWithUnavailableModifiers([]);
  },[setUnavailableItems, setItemsWithUnavailableModifiers]);

  useEffect(()=> {
    if (reorderFailureCode && totalItemCount > 0) {
      setReorderfailureCode(undefined);
    } else if ( reorderFailureCode ){
      heapTrack('reorder-item-failure-empty-cart-viewed', { type: reorderFailureCode });
    }
  }, [totalItemCount, reorderFailureCode, setReorderfailureCode]);

  return {
    orders: orders || [],
    loading: isLoading,
    error,
    unavailableItems,
    itemsWithUnavailableModifiers,
    reorderFailureCode,
    getConciseLineItems,
    addHistoricItemsToCart,
    clearUnavailableItems,
    checkItemsWithUnavailableModifiers
  };

});
